'use client';

import { useMemo } from 'react';

import type { WidgetConfigState } from '../fields';
import type { EmbedConfig, WidgetSchema } from '../types';
import { buildEmbedConfig } from '../embed/buildEmbedConfig';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { EmbedScriptFrame } from './EmbedScriptFrame';

/** Map the normalized EmbedConfig to the loader's inline JSON config shape. */
function toLoaderConfig(embed: EmbedConfig): Record<string, unknown> {
    const isTicker = embed.widgetSize === 'ticker';
    const loaderSize = embed.widgetSize === 'inline' ? 'small' : embed.widgetSize;

    const config: Record<string, unknown> = {
        eventTicker: isTicker ? (embed.category ?? '') : embed.eventTicker,
        colorTheme: embed.colorTheme,
        height: embed.height,
        width: embed.width,
        period: embed.period,
        widgetSize: loaderSize,
    };
    if (embed.variant) config.variant = embed.variant;
    if (embed.marketId) config.marketId = embed.marketId;
    if (isTicker) {
        config.marketIds = [embed.logoColor ? `logoColor:${embed.logoColor}` : '', `colorTheme:${embed.colorTheme}`]
            .filter(Boolean)
            .join(',');
    } else if (embed.marketIds) {
        config.marketIds = embed.marketIds;
    }
    if (embed.seriesTicker) config.seriesTicker = embed.seriesTicker;
    if (embed.useMostRecentInSeries) config.useMostRecentInSeries = true;
    if (embed.utmSource) config.utmSource = embed.utmSource;
    if (embed.utmCampaign) config.utmCampaign = embed.utmCampaign;
    if (embed.promoCode) config.promoCode = embed.promoCode;
    return config;
}

/** Free-text values (e.g. Partner ID) reach the preview only once typing pauses. */
const TEXT_FIELD_DEBOUNCE_MS = 600;

/**
 * `config` with its free-text fields debounced. Those values are part of the
 * iframe src, so without this every keystroke would reload the preview.
 * Toggles and pickers stay immediate.
 */
function usePreviewConfig(schema: WidgetSchema, config: WidgetConfigState): WidgetConfigState {
    const textValues = useMemo(
        () =>
            Object.fromEntries(
                schema.fields.filter((field) => field.kind === 'text').map((field) => [field.key, config[field.key]]),
            ),
        [schema, config],
    );
    const debouncedText = useDebouncedValue(JSON.stringify(textValues), TEXT_FIELD_DEBOUNCE_MS);
    return useMemo(() => ({ ...config, ...(JSON.parse(debouncedText) as WidgetConfigState) }), [config, debouncedText]);
}

export function WidgetPreview({ schema, config }: { schema: WidgetSchema; config: WidgetConfigState }) {
    const embed = buildEmbedConfig(schema, usePreviewConfig(schema, config));

    if (schema.preview.kind === 'direct-iframe') {
        // Mirror buildDirectIframeCode so the preview shows exactly what the embed will.
        const params = new URLSearchParams(embed.iframeParams ?? {});
        if (embed.colorTheme === 'dark') params.set('colorTheme', 'dark');
        const search = params.toString();
        const query = search ? `?${search}` : '';
        return (
            <iframe
                key={`${embed.iframeSrcPath}${query}`}
                src={`${embed.iframeSrcPath ?? ''}${query}`}
                title={schema.meta.title}
                loading="lazy"
                // Fill the preview frame (WidgetWorkbench sizes it: fixed height, or aspect ratio
                // for fixed-size widgets) so 16:9 widgets aren't letterboxed in a taller box.
                className="block h-full w-full border-0"
            />
        );
    }

    // Ticker tape renders via /banner (same as the iframe embed), not the event loader.
    if (schema.preview.kind === 'banner-loader') {
        const params = new URLSearchParams();
        if (embed.category) params.set('category', embed.category);
        params.set('colorTheme', embed.colorTheme);
        if (embed.logoColor) params.set('logocolor', embed.logoColor);
        if (embed.showLogo === false) params.set('showLogo', 'false');
        const src = `/banner?${params.toString()}`;
        return (
            <iframe
                key={src}
                src={src}
                title={schema.meta.title}
                loading="lazy"
                className="block w-full border-0"
                style={{ height: '60px' }}
            />
        );
    }

    // Inline pill: show it inside newspaper-style body copy so partners see the core
    // use case -- live odds dropped inline next to a candidate, right in an article.
    if (embed.widgetSize === 'inline' || embed.widgetSize === 'small') {
        return (
            <div className="flex min-h-[120px] w-full items-center justify-center p-8">
                <div className="max-w-xl text-[17px] leading-[1.7] text-text-x10">
                    With the 2028 Democratic primary still wide open,{' '}
                    <EmbedScriptFrame config={toLoaderConfig(embed)} inline /> has emerged as the early frontrunner,
                    narrowly ahead of a crowded field of contenders.
                </div>
            </div>
        );
    }

    // Medium (watchlist) fits its content height so a short list isn't padded out.
    return <EmbedScriptFrame config={toLoaderConfig(embed)} autoHeight={embed.widgetSize === 'medium'} />;
}
